'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useCallback } from 'react';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import type { ButtonProps } from '@mui/material/Button';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import type { EmblaCarouselType } from 'embla-carousel';
import { mergeSx } from '@/lib/mergeSx';
export type UsePrevNextButtonsType = {
  prevBtnDisabled: boolean;
  nextBtnDisabled: boolean;
  onPrevButtonClick: () => void;
  onNextButtonClick: () => void;
};
export function useCarouselNavigationButtons(emblaApi) {
  const $ = _c(9);
  let t0;
  if ($[0] !== emblaApi) {
    t0 = () => {
      if (!emblaApi) {
        return;
      }
      emblaApi.scrollPrev();
    };
    $[0] = emblaApi;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const onPrevButtonClick = t0;
  let t1;
  if ($[2] !== emblaApi) {
    t1 = () => {
      if (!emblaApi) {
        return;
      }
      emblaApi.scrollNext();
    };
    $[2] = emblaApi;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const onNextButtonClick = t1;
  const t2 = !emblaApi?.canScrollPrev();
  const t3 = !emblaApi?.canScrollNext();
  let t4;
  if ($[4] !== onNextButtonClick || $[5] !== onPrevButtonClick || $[6] !== t2 || $[7] !== t3) {
    t4 = {
      prevBtnDisabled: t2,
      nextBtnDisabled: t3,
      onPrevButtonClick,
      onNextButtonClick
    };
    $[4] = onNextButtonClick;
    $[5] = onPrevButtonClick;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
const CarouselNavigationButton = styled(Button, {
  name: 'Carousel',
  slot: 'navigationButton',
  skipSx: false
})({
  position: 'absolute',
  WebkitTapHighlightColor: 'rgba(var(--mui-palette-text-primary-channel) / 0.5)',
  appearance: 'none',
  touchAction: 'manipulation',
  display: 'inline-flex',
  textDecoration: 'none',
  cursor: 'pointer',
  border: 0,
  padding: 0,
  margin: 0,
  background: 'rgba(255, 255, 255, 0.6)',
  borderColor: 'var(--mui-palette-common-white)',
  borderStyle: 'solid',
  borderWidth: '0.2rem',
  boxShadow: 'var(--mui-shadows-2)',
  width: '3.6rem',
  height: '3.6rem',
  zIndex: 1,
  borderRadius: '50%',
  color: 'var(--mui-palette-common-white)',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 0,
  '&:disabled': {
    color: 'var(--mui-palette-action-disabled)'
  }
});
export function CarouselNextButton(t0) {
  const $ = _c(11);
  let children;
  let props;
  let sx;
  if ($[0] !== t0) {
    ({
      sx,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
    $[3] = sx;
  } else {
    children = $[1];
    props = $[2];
    sx = $[3];
  }
  let t1;
  if ($[4] !== sx) {
    t1 = mergeSx({
      top: "50%",
      right: "16px",
      transform: "translateY(-50%)"
    }, sx);
    $[4] = sx;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ArrowForwardIosIcon sx={{
      filter: "drop-shadow(0 0px 8px rgba(0,0,0,0.5))"
    }} />;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== children || $[8] !== props || $[9] !== t1) {
    t3 = <CarouselNavigationButton {...props} sx={t1}>{t2}{children}</CarouselNavigationButton>;
    $[7] = children;
    $[8] = props;
    $[9] = t1;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}
export function CarouselPrevButton(t0) {
  const $ = _c(11);
  let children;
  let props;
  let sx;
  if ($[0] !== t0) {
    ({
      sx,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
    $[3] = sx;
  } else {
    children = $[1];
    props = $[2];
    sx = $[3];
  }
  let t1;
  if ($[4] !== sx) {
    t1 = mergeSx({
      top: "50%",
      left: "16px",
      transform: "translateY(-50%)"
    }, sx);
    $[4] = sx;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ArrowBackIosNewIcon sx={{
      filter: "drop-shadow(0 0px 8px rgba(0,0,0,0.5))"
    }} />;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== children || $[8] !== props || $[9] !== t1) {
    t3 = <CarouselNavigationButton {...props} sx={t1}>{t2}{children}</CarouselNavigationButton>;
    $[7] = children;
    $[8] = props;
    $[9] = t1;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}